import type { Error as ErrorEnvelope } from '@/lib/api/generated/model';

/**
 * The refusals the secrets endpoints can answer with, one class each.
 *
 * Eight refusals, eight different things for the person to do, so eight types —
 * the same reasoning `features/workflows/errors.ts` applies to its four. A
 * single "could not save" would be worst for the refusal that matters most:
 * nothing about `PRODUCT_X_NEXT_TOKEN` looks wrong to the person who typed it,
 * so telling them the key is invalid sends them to fix something that is
 * already correct.
 *
 * The classes exist rather than a bare `code` string because the constitution
 * forbids narrowing an `unknown` catch without a type guard (§2.5), and because
 * the mapping then lives here instead of in every component that mutates.
 */

/** Which part of the form a refusal belongs to. */
export type SecretErrorField = 'key' | 'value' | 'description' | 'form';

class SecretError extends Error {
  readonly status: number;
  /** Where the dialog should render this (research.md D3). */
  readonly field: SecretErrorField;

  constructor(
    name: string,
    status: number,
    field: SecretErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.status = status;
    this.field = field;
  }
}

/** The key is not a usable environment variable name. */
export class SecretKeyInvalidError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretKeyInvalidError',
      422,
      'key',
      message ??
        'Use capital letters, digits and underscores, not starting with a digit.',
    );
  }
}

/**
 * The key is well-formed but the execution plane owns that name.
 *
 * Kept apart from {@link SecretKeyInvalidError} deliberately: this key is not
 * malformed, and saying it is would be a wrong diagnosis rather than a vague
 * one.
 */
export class SecretKeyReservedError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretKeyReservedError',
      422,
      'key',
      message ??
        'That name is reserved by the platform. Choose a different one.',
    );
  }
}

/** The workspace already has an entry with this key, of either kind. */
export class SecretKeyConflictError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretKeyConflictError',
      409,
      'key',
      message ??
        'This workspace already has an entry with that name. Secrets and variables share one list of names.',
    );
  }
}

/** The value is past the size limit. Nothing about the key is wrong. */
export class SecretValueTooLargeError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretValueTooLargeError',
      413,
      'value',
      message ?? 'That value is too large. The limit is 64 KB.',
    );
  }
}

/** The description is over length or carries a line break. */
export class SecretDescriptionInvalidError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretDescriptionInvalidError',
      422,
      'description',
      message ??
        'That description is too long or spans more than one line. Keep it under 500 characters, on a single line.',
    );
  }
}

/**
 * The workspace is full.
 *
 * Form-level, not on the key field: attaching this to the key input would imply
 * the name is the problem and send someone off to rename it.
 */
export class SecretLimitExceededError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretLimitExceededError',
      409,
      'form',
      message ??
        'This workspace has reached its limit of 200 entries. Delete one before adding another.',
    );
  }
}

/** The entry is gone — deleted from another tab, or by someone else. */
export class SecretNotFoundError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretNotFoundError',
      404,
      'form',
      message ?? 'That entry no longer exists. The list has been refreshed.',
    );
  }
}

/**
 * The service holds no encryption key, so nothing can be stored by anyone.
 *
 * Form-level and phrased as a server condition on purpose. The input was fine;
 * presenting this as user error would send someone editing a value that was
 * never the problem.
 */
export class SecretEncryptionUnavailableError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretEncryptionUnavailableError',
      500,
      'form',
      message ??
        'The service cannot store values right now. This is a server configuration problem, not something wrong with what you entered.',
    );
  }
}

/**
 * At least one LLM profile's credential override still points at this entry
 * (067 FR-010).
 *
 * Form-level: the conflict is not about anything typed into a field, it is
 * about a relationship the delete confirmation has no way to show ahead of
 * time.
 */
export class SecretInUseError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretInUseError',
      409,
      'form',
      message ??
        'This entry is still used as a credential override by at least one LLM profile. Remove that override before deleting it.',
    );
  }
}

/**
 * The key is a name a connection owns (`GH_TOKEN`…). Attributed to the key, like
 * the reserved-prefix refusal, but the way on is "connect the provider", which the
 * form offers as a button.
 */
export class SecretKeyManagedError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretKeyManagedError',
      422,
      'key',
      message ??
        'A connection manages this name. Connect the provider instead of adding it by hand.',
    );
  }
}

/** The entry belongs to a connection; it changes only through that connection. */
export class SecretManagedError extends SecretError {
  constructor(message?: string) {
    super(
      'SecretManagedError',
      409,
      'form',
      message ??
        'This entry is managed by a connection. Change it from the connection instead.',
    );
  }
}

/**
 * A refusal this build does not recognise — a rule the service grew later.
 *
 * Form-level, and deliberately vague: claiming a cause it cannot know would be
 * worse than admitting it does not know one (FR-018).
 */
export class SecretRequestFailedError extends SecretError {
  constructor(status: number, message?: string) {
    super(
      'SecretRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => SecretError> = {
  SECRET_KEY_INVALID: (m) => new SecretKeyInvalidError(m),
  SECRET_KEY_RESERVED: (m) => new SecretKeyReservedError(m),
  SECRET_KEY_CONFLICT: (m) => new SecretKeyConflictError(m),
  SECRET_VALUE_TOO_LARGE: (m) => new SecretValueTooLargeError(m),
  SECRET_DESCRIPTION_INVALID: (m) => new SecretDescriptionInvalidError(m),
  SECRET_LIMIT_EXCEEDED: (m) => new SecretLimitExceededError(m),
  SECRET_NOT_FOUND: (m) => new SecretNotFoundError(m),
  SECRET_KEY_MANAGED: (m) => new SecretKeyManagedError(m),
  SECRET_MANAGED: (m) => new SecretManagedError(m),
  SECRET_ENCRYPTION_UNAVAILABLE: (m) => new SecretEncryptionUnavailableError(m),
  SECRET_IN_USE: (m) => new SecretInUseError(m),
  WORKSPACE_NOT_FOUND: () =>
    new SecretNotFoundError('This workspace no longer exists.'),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is deliberately **not** passed through. The
 * classes carry messages written for the person looking at the form; the
 * service's are written for whoever reads a log, and rendering those would
 * couple this UI to backend prose. The envelope's `code` is what this reads —
 * that is the part the contract promises to keep stable.
 *
 * An unrecognised code produces {@link SecretRequestFailedError} rather than
 * throwing, so a rule added to the service later degrades to a readable general
 * error instead of a blank screen.
 */
export function toSecretError(status: number, body: unknown): SecretError {
  const code =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as ErrorEnvelope).error
      : undefined;

  const construct = code ? BY_CODE[code] : undefined;
  return construct ? construct() : new SecretRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isSecretError(error: unknown): error is SecretError {
  return error instanceof SecretError;
}

export type { SecretError };
